/* =========================================================
   AURMANS — product.js (loaded by sections/main-product only)
   Gallery (swipe / thumbnails / arrows), zoom lightbox, sticky
   add-to-cart bar, delivery estimate, real pack savings, bundle
   prices (custom.bundle_pct), review
   sorting/filtering (folded into one row on phones) and custom
   analytics events.
   Variant logic and the add-to-cart request stay in theme.js;
   this file listens to its events:
     aur:variant-change {variant, userAction} · aur:cart-adding
     aur:cart-added {item} · aur:cart-error {message}
   Everything degrades to plain, usable HTML without JS.
   ========================================================= */
(function () {
  'use strict';

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  var STRINGS = (function () {
    var el = document.getElementById('aurmans-strings');
    try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; }
  })();
  function str(key, fallback) { return STRINGS[key] || fallback; }

  function money(cents, currency) {
    return (cents / 100).toLocaleString(document.documentElement.lang || 'en', {
      style: 'currency', currency: currency || 'EUR'
    });
  }

  function publish(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) { /* analytics must never break the page */ }
  }

  /* ---------- Gallery ---------- */
  function initGallery(pdp, lightbox) {
    var gallery = qs('[data-pdp-gallery]', pdp);
    var track = gallery && qs('[data-gallery-track]', gallery);
    if (!track) return null;
    var slides = qsa('.pdp-gallery__slide', track);
    var thumbs = qsa('[data-thumb-index]', gallery);
    var dots = qsa('.pdp-gallery__dot', gallery);
    var counter = qs('[data-gallery-counter]', gallery);
    var current = 0;

    function setActive(i) {
      current = i;
      thumbs.forEach(function (t, n) { t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
      dots.forEach(function (d, n) { d.classList.toggle('is-active', n === i); });
      if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
      var thumb = thumbs[i];
      var strip = thumb && thumb.closest('.pdp-gallery__thumbs');
      if (strip && strip.scrollWidth > strip.clientWidth + 1) {
        // phones: the thumbnails are a horizontal strip under the photo; keep the active one in view
        var tr = thumb.getBoundingClientRect();
        var sr = strip.getBoundingClientRect();
        if (tr.left < sr.left || tr.right > sr.right) {
          strip.scrollTo({ left: strip.scrollLeft + tr.left - sr.left - (sr.width - tr.width) / 2, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      } else if (strip && strip.scrollHeight > strip.clientHeight) {
        var li = thumb.parentNode;
        if (li.offsetTop < strip.scrollTop || li.offsetTop + li.offsetHeight > strip.scrollTop + strip.clientHeight) {
          strip.scrollTo({ top: li.offsetTop - 8, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      }
    }

    function goTo(i, smooth) {
      if (!slides.length) return;
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      setActive(i);
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var w = track.clientWidth || 1;
        var i = Math.round(track.scrollLeft / w);
        if (i !== current && i >= 0 && i < slides.length) setActive(i);
      });
    }, { passive: true });

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { goTo(parseInt(t.getAttribute('data-thumb-index'), 10), true); });
    });
    var prev = qs('[data-gallery-prev]', gallery);
    var next = qs('[data-gallery-next]', gallery);
    if (prev) prev.addEventListener('click', function () { goTo(current - 1, true); });
    if (next) next.addEventListener('click', function () { goTo(current + 1, true); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - 1, true); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + 1, true); }
    });

    qsa('[data-gallery-open]', gallery).forEach(function (b) {
      b.addEventListener('click', function () {
        if (lightbox) lightbox.open(parseInt(b.getAttribute('data-gallery-open'), 10), b);
      });
    });
    if (!lightbox) gallery.classList.add('no-zoom');

    // Keep the slide in place when the layout changes width (rotation, resize).
    var lastW = track.clientWidth;
    window.addEventListener('resize', function () {
      if (track.clientWidth !== lastW) { lastW = track.clientWidth; goTo(current, false); }
    });

    // The gallery opens on the first image; it shows a variant's image only when the customer picks it.
    pdp.addEventListener('aur:variant-change', function (e) {
      var v = e.detail && e.detail.variant;
      if (!v || !v.featured_media || !e.detail.userAction) return;
      for (var n = 0; n < slides.length; n++) {
        if (String(slides[n].getAttribute('data-media-id')) === String(v.featured_media.id)) { goTo(n, true); break; }
      }
    });
    return { goTo: goTo, current: function () { return current; } };
  }

  /* ---------- Zoom lightbox (native <dialog>) ---------- */
  function initLightbox(pdp) {
    var dlg = qs('[data-lightbox]', pdp);
    if (!dlg || typeof dlg.showModal !== 'function') return null;
    var track = qs('[data-lightbox-track]', dlg);
    var slides = qsa('.pdp-lightbox__slide', track);
    var counter = qs('[data-lightbox-counter]', dlg);
    var current = 0;
    var opener = null;
    var onClose = null;

    function load(i) {
      var img = slides[i] && qs('img', slides[i]);
      if (img && !img.getAttribute('src') && img.getAttribute('data-src')) img.setAttribute('src', img.getAttribute('data-src'));
    }
    function setActive(i) {
      current = i;
      load(i); load(i + 1); load(i - 1);
      if (counter) counter.textContent = (i + 1) + ' / ' + slides.length;
    }
    function go(i, smooth) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
      setActive(i);
    }
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var i = Math.round(track.scrollLeft / (track.clientWidth || 1));
        if (i !== current && i >= 0 && i < slides.length) setActive(i);
      });
    }, { passive: true });

    var closeBtn = qs('[data-lightbox-close]', dlg);
    var prev = qs('[data-lightbox-prev]', dlg);
    var next = qs('[data-lightbox-next]', dlg);
    if (closeBtn) closeBtn.addEventListener('click', function () { dlg.close(); });
    if (prev) prev.addEventListener('click', function () { go(current - 1, true); });
    if (next) next.addEventListener('click', function () { go(current + 1, true); });
    dlg.addEventListener('click', function (e) { if (e.target === dlg || e.target.classList.contains('pdp-lightbox__slide')) dlg.close(); });
    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') go(current - 1, true);
      if (e.key === 'ArrowRight') go(current + 1, true);
    });
    dlg.addEventListener('close', function () {
      document.documentElement.classList.remove('pdp-lock');
      if (onClose) onClose(current);
      if (opener) opener.focus({ preventScroll: true });
    });

    return {
      open: function (i, from) {
        opener = from || null;
        setActive(i);
        dlg.showModal();
        document.documentElement.classList.add('pdp-lock');
        window.requestAnimationFrame(function () { go(i, false); });
      },
      onClose: function (fn) { onClose = fn; }
    };
  }

  /* ---------- Pack savings ("€25.00 each · Save 26%"), only from real variant prices ---------- */
  function initPackPricing(picker, variants, currency) {
    var template = picker.getAttribute('data-pack-label');
    if (!template) return function () {};
    var groups = qsa('[data-option-index]', picker);
    var packIdx = -1;
    var packGroup = null;
    var units = {};
    groups.forEach(function (g) {
      if (packIdx !== -1) return;
      var pills = qsa('button[data-option-value]', g);
      if (pills.length < 2) return;
      var map = {};
      var ok = pills.every(function (p) {
        var m = /^\s*(\d+)/.exec(p.getAttribute('data-option-value'));
        if (!m) return false;
        map[p.getAttribute('data-option-value')] = parseInt(m[1], 10);
        return true;
      });
      var hasSingle = Object.keys(map).some(function (k) { return map[k] === 1; });
      if (ok && hasSingle) { packIdx = parseInt(g.getAttribute('data-option-index'), 10); packGroup = g; units = map; }
    });
    if (packIdx === -1) return function () {};
    var singleValue = Object.keys(units).filter(function (k) { return units[k] === 1; })[0];

    function find(value, base) {
      return variants.filter(function (v) {
        if (v.options[packIdx] !== value) return false;
        for (var i = 0; i < v.options.length; i++) {
          if (i !== packIdx && v.options[i] !== base.options[i]) return false;
        }
        return true;
      })[0];
    }

    return function update(variant) {
      var single = find(singleValue, variant);
      qsa('button[data-option-value]', packGroup).forEach(function (p) {
        var meta = qs('[data-pill-meta]', p);
        if (!meta) return;
        var n = units[p.getAttribute('data-option-value')];
        var v = find(p.getAttribute('data-option-value'), variant);
        if (!v || !single || n <= 1) { meta.hidden = true; return; }
        var per = v.price / n;
        var pct = Math.round((1 - per / single.price) * 100);
        if (!(pct > 0)) { meta.hidden = true; return; }
        meta.textContent = template.replace('__PRICE__', money(Math.round(per), currency)).replace('__PERCENT__', pct);
        meta.hidden = false;
      });
    };
  }

  /* ---------- Delivery estimate from the theme settings ---------- */
  function initDelivery(pdp) {
    var el = qs('[data-delivery]', pdp);
    if (!el || !window.Intl) return;
    var min = parseInt(el.getAttribute('data-min'), 10);
    var max = parseInt(el.getAttribute('data-max'), 10);
    var tpl = el.getAttribute('data-template');
    var out = qs('[data-delivery-text]', el);
    if (!tpl || !out || !(min > 0) || !(max >= min)) return;
    try {
      var fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'en', { weekday: 'short', day: 'numeric', month: 'short' });
      var d1 = new Date(); d1.setDate(d1.getDate() + min);
      var d2 = new Date(); d2.setDate(d2.getDate() + max);
      // Parcels aren't delivered on Sundays: move an estimate that lands on one to Monday.
      [d1, d2].forEach(function (d) { if (d.getDay() === 0) d.setDate(d.getDate() + 1); });
      out.textContent = tpl.replace('__FROM__', fmt.format(d1)).replace('__TO__', fmt.format(d2));
    } catch (e) { /* keep the "7–15 days" text */ }
  }

  /* ---------- Buy box: price on the button, confirmation, proxies (sticky bar + final CTA) ---------- */
  function initBuyBox(pdp) {
    var picker = qs('[data-variant-picker]', pdp);
    var form = picker && qs('form[data-product-form]', picker);
    var addBtn = form && qs('[data-add-btn]', form);
    if (!picker || !form || !addBtn) return;

    var variants = [];
    try { variants = JSON.parse(picker.getAttribute('data-variants')) || []; } catch (e) { variants = []; }
    var currency = picker.getAttribute('data-currency') || 'EUR';
    var idInput = qs('[data-variant-id]', form);
    var qtyInput = qs('[data-qty-input-main]', form);
    var addPrice = qs('[data-add-price]', addBtn);
    var panel = qs('[data-added-panel]', form);
    var proxies = qsa('[data-buy-proxy]');
    var stickyVariant = qs('[data-sticky-variant]', pdp);
    var stickyPrice = qs('[data-sticky-price]', pdp);
    var finalVariant = qs('[data-final-variant]');
    var finalPrice = qs('[data-final-price]');
    var updatePacks = initPackPricing(picker, variants, currency);
    var tiersEl = qs('[data-tiers]', form);
    var tierBtns = tiersEl ? qsa('[data-tier-qty]', tiersEl) : [];
    var bundles = {};
    try { bundles = JSON.parse(picker.getAttribute('data-bundles') || '{}') || {}; } catch (e) { bundles = {}; }
    var priceEl = qs('[data-variant-price]', picker);
    var bundleBadge = qs('[data-bundle-badge]', picker);
    var bundleNote = qs('[data-bundle-note]', picker);

    // Shopify takes a percentage discount off each unit and rounds it down to the cent.
    function afterPct(cents, pct) { return cents - Math.floor(cents * pct / 100); }
    // Bundle variants (custom.bundle_pct): an automatic discount on that variant, applied in the cart.
    function bundlePct(v) { return v ? (parseInt(bundles[v.id], 10) || 0) : 0; }
    // Shopify applies the best automatic discount, never two at once.
    function pctFor(v, q) { return Math.max(tierPct(q), bundlePct(v)); }
    function syncBundle(v) {
      var pct = bundlePct(v);
      [bundleBadge, bundleNote].forEach(function (el) {
        if (!el) return;
        el.hidden = !(pct > 0);
        if (pct > 0) el.textContent = (el.getAttribute('data-label') || '-__PERCENT__%').replace('__PERCENT__', pct);
      });
      // theme.js has just written the plain price; show the bundle price on top of it.
      if (!priceEl || !(pct > 0)) return;
      var was = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;
      priceEl.innerHTML = '<s>' + money(was, currency) + '</s>' + money(afterPct(v.price, pct), currency);
      priceEl.classList.add('product-price--sale');
    }

    // Multi-buy tiers: the best tier reached by the quantity (the cart's automatic discount does the same).
    function tierFor(q) {
      var best = null;
      tierBtns.forEach(function (b) { if (q >= parseInt(b.getAttribute('data-tier-qty'), 10)) best = b; });
      return best;
    }
    function tierPct(q) { var b = tierFor(q); return b ? parseInt(b.getAttribute('data-tier-pct'), 10) || 0 : 0; }
    function syncTiers(v) {
      if (!tierBtns.length) return;
      var active = tierFor(target());
      var label = tiersEl.getAttribute('data-each-label') || '__PRICE__';
      tierBtns.forEach(function (b) {
        var pct = parseInt(b.getAttribute('data-tier-pct'), 10) || 0;
        var each = qs('[data-tier-each]', b);
        if (each) each.textContent = label.replace('__PRICE__', money(afterPct(v.price, pct), currency));
        b.classList.toggle('is-active', b === active);
        b.setAttribute('aria-pressed', b === active ? 'true' : 'false');
      });
    }
    // Pack builder (data-mix): with a tier of 2+ chosen, the customer picks how many of each variant. The lines
    // are sent together (aur:cart-items) and the cart's automatic discount counts them as one product.
    var mixEl = qs('[data-mix]', form);
    var mixRows = mixEl ? qsa('[data-mix-row]', mixEl) : [];
    var stepperEl = qs('[data-qty-stepper]', form);
    var mix = { on: false, pack: 1, counts: {} };
    var mixLocked = false;
    var MIX_MAX = parseInt(mixEl && mixEl.getAttribute('data-max-open'), 10) || 12;

    function lastTier() { return tierBtns.length ? parseInt(tierBtns[tierBtns.length - 1].getAttribute('data-tier-qty'), 10) : 1; }
    function mixTotal() { var t = 0; Object.keys(mix.counts).forEach(function (k) { t += mix.counts[k]; }); return t; }
    function mixOpen() { return mix.pack >= lastTier(); } // the last tier ("4+") takes any total from there up
    function mixCap() { return mixOpen() ? MIX_MAX : mix.pack; }
    function mixComplete() { var t = mixTotal(); return mixOpen() ? t >= mix.pack : t === mix.pack; }
    function variantById(id) { return variants.filter(function (v) { return v.id === id; })[0]; }
    function mixItems() {
      return Object.keys(mix.counts).filter(function (k) { return mix.counts[k] > 0; })
        .map(function (k) { return { id: parseInt(k, 10), quantity: mix.counts[k] }; });
    }
    function mixCents() {
      var pct = tierPct(mixTotal());
      return mixItems().reduce(function (sum, it) { var v = variantById(it.id); return sum + (v ? afterPct(v.price, pct) * it.quantity : 0); }, 0);
    }
    function mixNames() {
      return mixItems().map(function (it) { var v = variantById(it.id); return (v ? v.title : '') + (it.quantity > 1 ? ' ×' + it.quantity : ''); }).join(', ');
    }
    // What the pack is for: the tier chosen while building a mix, otherwise the quantity.
    function target() { return mix.on ? mix.pack : qty(); }

    function renderMix() {
      if (!mixEl) return;
      var total = mixTotal();
      mixEl.hidden = !mix.on;
      picker.classList.toggle('is-mix', mix.on);
      if (stepperEl) stepperEl.hidden = mix.on;
      mixRows.forEach(function (row) {
        var id = parseInt(row.getAttribute('data-variant-id'), 10);
        var v = variantById(id);
        var n = mix.counts[id] || 0;
        qs('[data-mix-qty]', row).textContent = n;
        qs('[data-mix-dec]', row).disabled = n < 1;
        qs('[data-mix-inc]', row).disabled = !v || !v.available || total >= mixCap();
        row.classList.toggle('has-qty', n > 0);
        row.classList.toggle('is-unavailable', !v || !v.available);
      });
      var summary = qs('[data-mix-summary]', mixEl);
      if (summary && mix.on) {
        var line = (mixEl.getAttribute('data-label-summary') || '__N__/__TOTAL__').replace('__N__', total).replace('__TOTAL__', mix.pack + (mixOpen() ? '+' : ''));
        var missing = mix.pack - total;
        if (missing > 0) line += ' · ' + (mixEl.getAttribute('data-label-more') || '__N__').replace('__N__', missing);
        summary.textContent = line;
        summary.classList.toggle('is-complete', mixComplete());
      }
      // keep the quantity field in step so the rest of the buy box (price, sticky bar) reads the pack size
      if (mix.on && qtyInput) qtyInput.value = Math.max(total, 1);
    }
    // Set the pack size: trim extras from the last variants, top up with the variant selected above.
    function setPack(n) {
      mix.pack = Math.max(2, Math.min(n, lastTier()));
      var cap = mixCap();
      var ids = Object.keys(mix.counts);
      var total = mixTotal();
      for (var i = ids.length - 1; i >= 0 && total > cap; i--) {
        var take = Math.min(mix.counts[ids[i]], total - cap);
        mix.counts[ids[i]] -= take;
        total -= take;
      }
      if (total < n) {
        var cur = currentVariant().id;
        mix.counts[cur] = Math.min((mix.counts[cur] || 0) + (n - total), cap);
      }
    }
    function startMix(n) {
      mix.on = true;
      mix.counts = {};
      setPack(n);
      renderMix();
    }
    function selectVariant(v) {
      var group = qs('[data-option-index="0"]', form);
      var sel = group && qs('select[data-option-value]', group);
      if (sel) { sel.value = v.options[0]; sel.dispatchEvent(new Event('change', { bubbles: true })); return; }
      var pill = group && qsa('button[data-option-value]', group).filter(function (b) { return b.getAttribute('data-option-value') === v.options[0]; })[0];
      if (pill) pill.click();
    }
    function stopMix() {
      var best = null;
      Object.keys(mix.counts).forEach(function (k) { if (!best || mix.counts[k] > mix.counts[best]) best = k; });
      var v = best ? variantById(parseInt(best, 10)) : null;
      mix.on = false; mix.pack = 1; mix.counts = {};
      if (qtyInput) qtyInput.value = 1;
      renderMix();
      if (v && v.id !== currentVariant().id) selectVariant(v);
    }

    tierBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        if (!qtyInput) return;
        var n = parseInt(b.getAttribute('data-tier-qty'), 10);
        if (mixEl && n > 1) {
          if (mix.on) { setPack(n); renderMix(); } else { startMix(n); }
        } else {
          if (mix.on) stopMix();
          qtyInput.value = n;
        }
        sync(currentVariant());
        publish('aurmans_tier_selected', { product_id: pdp.getAttribute('data-product-id'), quantity: qtyInput.value });
      });
    });
    mixRows.forEach(function (row) {
      var id = parseInt(row.getAttribute('data-variant-id'), 10);
      qs('[data-mix-inc]', row).addEventListener('click', function () {
        if (mixTotal() >= mixCap()) return;
        mix.counts[id] = (mix.counts[id] || 0) + 1;
        renderMix(); sync(currentVariant());
      });
      qs('[data-mix-dec]', row).addEventListener('click', function () {
        if (!mix.counts[id]) return;
        mix.counts[id] -= 1;
        renderMix(); sync(currentVariant());
      });
    });
    if (mixEl) {
      // An incomplete pack can't be added; the summary already says how many are missing.
      form.addEventListener('submit', function (e) {
        if (!mix.on || (e.submitter && !e.submitter.hasAttribute('data-add-btn'))) return;
        if (!mixComplete()) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);
      form.addEventListener('aur:cart-items', function (e) {
        if (mix.on && mixComplete()) e.detail.items = mixItems();
      });
    }

    function currentVariant() {
      var id = idInput ? parseInt(idInput.value, 10) : NaN;
      return variants.filter(function (v) { return v.id === id; })[0] || variants[0];
    }
    function qty() { return Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1); }

    function proxyLabel(text) {
      proxies.forEach(function (p) { var l = qs('[data-proxy-label]', p) || p; l.textContent = text; });
    }
    function syncProxies(v) {
      proxies.forEach(function (p) { p.disabled = !v.available || (mix.on && !mixComplete()); });
      proxyLabel(v.available ? addBtn.getAttribute('data-label-add') : addBtn.getAttribute('data-label-sold-out'));
    }

    function sync(v) {
      if (!v) return;
      if (addPrice) {
        addPrice.textContent = money(mix.on ? mixCents() : afterPct(v.price, pctFor(v, qty())) * qty(), currency);
        addPrice.hidden = !v.available || (mix.on && !mixTotal());
      }
      // A pack that isn't complete yet can't be added.
      var lock = mix.on && !mixComplete();
      if (lock || mixLocked) {
        if (addBtn.getAttribute('aria-busy') !== 'true') addBtn.disabled = lock || !v.available;
        mixLocked = lock;
      }
      var title = mix.on ? mixNames() : v.title;
      // The proxies buy the quantity chosen above, so say so when it is more than one.
      var priceText = mix.on ? money(mixCents(), currency) : (qty() > 1 ? qty() + ' × ' : '') + money(afterPct(v.price, bundlePct(v)), currency);
      if (stickyVariant) stickyVariant.textContent = title;
      if (finalVariant) finalVariant.textContent = title;
      if (stickyPrice) stickyPrice.textContent = priceText;
      if (finalPrice) finalPrice.textContent = priceText;
      syncProxies(v);
      updatePacks(v);
      syncTiers(v);
      syncBundle(v);
    }

    picker.addEventListener('aur:variant-change', function (e) {
      var v = e.detail && e.detail.variant;
      sync(v);
      if (panel) panel.hidden = true;
      if (v && e.detail.userAction) {
        publish('aurmans_variant_selected', { product_id: pdp.getAttribute('data-product-id'), variant_id: v.id, variant_title: v.title, price: v.price / 100, currency: currency });
      }
    });
    if (qtyInput) {
      // A quantity of 2+ on a product with a pack builder opens it, with that many of the selected variant.
      function onQty() {
        if (mixEl && !mix.on && qty() >= 2) startMix(qty());
        sync(currentVariant());
      }
      qtyInput.addEventListener('change', onQty);
      qtyInput.addEventListener('input', onQty);
    }

    form.addEventListener('aur:cart-adding', function () {
      proxies.forEach(function (p) { p.disabled = true; });
      proxyLabel(str('adding', 'Adding…'));
    });
    form.addEventListener('aur:cart-added', function () {
      if (panel) panel.hidden = false;
      proxyLabel(str('added', 'Added'));
      setTimeout(function () { syncProxies(currentVariant()); }, 1800);
    });
    form.addEventListener('aur:cart-error', function () { syncProxies(currentVariant()); });

    proxies.forEach(function (p) {
      p.addEventListener('click', function () {
        if (addBtn.disabled) return;
        publish('aurmans_proxy_add_to_cart', { product_id: pdp.getAttribute('data-product-id'), variant_id: idInput && idInput.value, source: p.closest('[data-sticky-atc]') ? 'sticky_bar' : 'final_cta' });
        if (typeof form.requestSubmit === 'function') form.requestSubmit(addBtn);
        else addBtn.click();
      });
    });

    // Products sold in packs (data-min-qty on the tiers): the pack builder opens with the minimum already chosen.
    var minQty = tiersEl ? (parseInt(tiersEl.getAttribute('data-min-qty'), 10) || 1) : 1;
    if (mixEl && minQty > 1) startMix(minQty);

    sync(currentVariant());
  }

  /* ---------- Sticky add-to-cart bar ---------- */
  function initSticky(pdp) {
    var bar = qs('[data-sticky-atc]', pdp);
    var addBtn = qs('form[data-product-form] [data-add-btn]', pdp);
    if (!bar || !addBtn || !('IntersectionObserver' in window)) return;
    bar.hidden = false;
    var footer = qs('.site-footer');
    var finalCta = qs('[data-pdp-final]');
    var state = { past: false, footer: false, final: false };

    function update() {
      var show = state.past && !state.footer && !state.final;
      bar.classList.toggle('is-visible', show);
      document.documentElement.classList.toggle('has-pdp-sticky', show);
    }
    // "Past" = the main button is above the top of the screen. The observed area is stretched far
    // downwards so the state only flips at the top edge, which every jump (anchor link,
    // scroll-to-top) crosses; a plain viewport observer misses jumps that skip over the button.
    new IntersectionObserver(function (entries) {
      var en = entries[entries.length - 1];
      state.past = !en.isIntersecting && en.boundingClientRect.top < 0;
      update();
    }, { rootMargin: '0px 0px 100000px 0px' }).observe(addBtn);
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.target === footer) state.footer = en.isIntersecting;
        else if (en.target === finalCta) state.final = en.isIntersecting;
      });
      update();
    });
    if (footer) io.observe(footer);
    if (finalCta) io.observe(finalCta);
  }

  /* ---------- Reviews: filter by stars, show more (the sort select was removed from the section;
     its handler below only runs if a [data-review-sort] is ever added back) ---------- */
  function initReviews() {
    var root = qs('[data-pdp-reviews]');
    var list = root && qs('[data-review-list]', root);
    if (!list) return;
    var items = qsa('.pdp-review', list);
    var more = qs('[data-review-more]', root);
    var sort = qs('[data-review-sort]', root);
    var clear = qs('[data-review-clear]', root);
    var filterBtns = qsa('[data-review-filter]', root);
    var tools = qs('.pdp-reviews__tools', root);
    var PAGE = 6;
    var shown = PAGE;
    var filter = 0;

    function byDate(a, b) { return a.getAttribute('data-date') < b.getAttribute('data-date') ? 1 : -1; }
    function apply() {
      var visible = items.filter(function (li) { return !filter || parseInt(li.getAttribute('data-rating'), 10) === filter; });
      items.forEach(function (li) { li.hidden = true; });
      visible.forEach(function (li, i) { li.hidden = i >= shown; });
      if (more) more.hidden = visible.length <= shown;
      if (clear) clear.hidden = !filter;
      // the tools row only holds "Show all" (no sort select), so it shows only while a star filter is on
      if (tools) tools.hidden = !filter;
      filterBtns.forEach(function (b) { b.setAttribute('aria-pressed', parseInt(b.getAttribute('data-review-filter'), 10) === filter ? 'true' : 'false'); });
    }
    if (sort) {
      sort.addEventListener('change', function () {
        var mode = sort.value;
        items.sort(function (a, b) {
          var ra = parseInt(a.getAttribute('data-rating'), 10);
          var rb = parseInt(b.getAttribute('data-rating'), 10);
          if (mode === 'highest' && ra !== rb) return rb - ra;
          if (mode === 'lowest' && ra !== rb) return ra - rb;
          return byDate(a, b);
        });
        items.forEach(function (li) { list.appendChild(li); });
        shown = PAGE;
        apply();
      });
    }
    if (more) {
      more.addEventListener('click', function () {
        var before = items.filter(function (li) { return !li.hidden; }).length;
        shown += PAGE;
        apply();
        var firstNew = items.filter(function (li) { return !li.hidden; })[before];
        if (firstNew) { firstNew.setAttribute('tabindex', '-1'); firstNew.focus({ preventScroll: true }); }
      });
    }
    filterBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        var n = parseInt(b.getAttribute('data-review-filter'), 10);
        filter = filter === n ? 0 : n;
        shown = PAGE;
        apply();
      });
    });
    if (clear) clear.addEventListener('click', function () { filter = 0; shown = PAGE; apply(); });
    apply();
  }

  /* ---------- Phones: reviews right under the buy buttons ----------
     When the product has reviews (the fold row exists), the reviews section moves into the slot under the
     add-to-cart / express buttons on phones, and back to its own place on wider screens. */
  function initReviewsPlacement() {
    var root = qs('[data-pdp-reviews]');
    var slot = qs('[data-reviews-slot]');
    if (!root || !slot || !qs('[data-reviews-toggle]', root) || !window.matchMedia) return;
    var home = document.createComment('reviews');
    root.parentNode.insertBefore(home, root);
    var phone = window.matchMedia('(max-width: 899px)');
    function place() {
      if (phone.matches) {
        if (root.parentNode !== slot) slot.appendChild(root);
      } else if (root.parentNode === slot) {
        home.parentNode.insertBefore(root, home.nextSibling);
      }
    }
    if (phone.addEventListener) phone.addEventListener('change', place);
    else if (phone.addListener) phone.addListener(place);
    place();
  }

  /* ---------- Phones: reviews fold into one row under the product ---------- */
  function initReviewsFold() {
    var root = qs('[data-pdp-reviews]');
    var toggle = root && qs('[data-reviews-toggle]', root);
    var panel = root && qs('[data-reviews-panel]', root);
    if (!toggle || !panel || !window.matchMedia) return;
    var phone = window.matchMedia('(max-width: 899px)');
    var open = location.hash === '#reviews';
    var pdp = qs('[data-pdp]');

    function render() {
      var fold = phone.matches;
      root.classList.toggle('is-foldable', fold);
      toggle.hidden = !fold;
      panel.hidden = fold && !open;
      toggle.setAttribute('aria-expanded', fold && !open ? 'false' : 'true');
    }
    toggle.addEventListener('click', function () {
      open = !open;
      render();
      if (open) publish('aurmans_reviews_opened', { product_id: pdp && pdp.getAttribute('data-product-id') });
    });
    // The rating under the product title links to #reviews: open them before the page jumps there.
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a[href="#reviews"]');
      if (link && !open) { open = true; render(); }
    });
    if (phone.addEventListener) phone.addEventListener('change', render);
    else if (phone.addListener) phone.addListener(render);
    render();
  }

  function init() {
    qsa('[data-pdp]').forEach(function (pdp) {
      var lightbox = initLightbox(pdp);
      var gallery = initGallery(pdp, lightbox);
      if (lightbox && gallery) lightbox.onClose(function (i) { gallery.goTo(i, false); });
      initDelivery(pdp);
      initBuyBox(pdp);
      initSticky(pdp);
    });
    initReviewsPlacement();
    initReviews();
    initReviewsFold();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
